import { useCallback, useEffect, useState } from 'react';

import { Item as BasketItem } from '../../../../weborder/BasketControl';
import { getCrossSellMenuItemIds } from '../../../api/menu/menu.service';
import { Menu, MenuItem } from '../../../api/menu/menu.types';
import { AsyncIdle, AxiosError, Status } from '../../../types/data.types';

interface UseCrossSellMenuItems {
  data: MenuItem[];
  getData: () => Promise<MenuItem[]>;
  isEmpty: boolean;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  showData: boolean;
}

interface Props {
  basketItems: BasketItem[];
  isAutoFetchEnabled?: boolean;
  limit?: number;
  menu: Menu;
  orderTotal: number;
}

export const useCrossSellMenuItems = (props: Props): UseCrossSellMenuItems => {
  const {
    basketItems,
    isAutoFetchEnabled = true,
    limit = 5,
    menu,
    orderTotal,
  } = props;

  const [crossSellMenuItems, setCrossSellMenuItems] = useState<
    AsyncIdle<MenuItem[]>
  >({
    status: isAutoFetchEnabled ? Status.LOADING : Status.IDLE,
  });
  const { status } = crossSellMenuItems;
  const isError = status === Status.ERROR;
  const isLoading = status === Status.LOADING;
  const isSuccess = status === Status.SUCCESS;
  const data = isSuccess ? crossSellMenuItems.data : [];
  const isEmpty = isSuccess && data.length === 0;
  const showData = isLoading || (isSuccess && !isEmpty);

  const getMenuItems = (menuItemIds: MenuItem['MenuItemId'][]): MenuItem[] => {
    const menuItems: MenuItem[] = [];
    menu.MenuSections.forEach((section) => {
      section.MenuItems.forEach((item) => {
        const isMenuItemFound = menuItemIds.some(
          (id) => id === item.MenuItemId,
        );
        if (isMenuItemFound) {
          menuItems.push(item);
        }
      });
    });

    return menuItems;
  };

  const getData = useCallback((): Promise<MenuItem[]> => {
    if (!isLoading) {
      setCrossSellMenuItems({ status: Status.LOADING });
    }

    const basketItemIds = basketItems.map(
      (basketItem) => basketItem.Item.MenuItemId,
    );
    const queryParams = {
      limit,
      menuId: menu.MenuId,
      menuItemId: basketItemIds,
      totalValue: orderTotal,
    };

    return getCrossSellMenuItemIds(queryParams)
      .then((response) => getMenuItems(response?.MenuItemIds || []))
      .then((menuItems) => {
        setCrossSellMenuItems({ status: Status.SUCCESS, data: menuItems });
        return menuItems;
      })
      .catch((error: AxiosError) => {
        setCrossSellMenuItems({ status: Status.ERROR, error });
        return [];
      });
  }, [basketItems.length, limit, menu?.MenuId, orderTotal]);

  useEffect(() => {
    const hasMenuData =
      !!menu?.MenuId && basketItems.length > 0 && orderTotal >= 0;
    if (isAutoFetchEnabled && !isSuccess && hasMenuData) {
      getData();
    }
  }, [basketItems.length, limit, menu?.MenuId, orderTotal]);

  return {
    data,
    getData,
    isEmpty,
    isError,
    isLoading,
    isSuccess,
    showData,
  };
};
